import { useRef, useState } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const HERO_IMG = "https://images.unsplash.com/photo-1653669485183-f4adf5806da9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MTJ8MHwxfHNlYXJjaHw0fHxkaXZlcnNlJTIwd29tZW4lMjBsYXVnaGluZyUyMGhvbWUlMjBsYXB0b3AlMjBncmFuZG1vdGhlciUyMG1vdGhlcnxlbnwwfHx8fDE3OTE4NDQ5MDF8MA&ixlib=rb-4.1.0&q=85";

const D = 1.9; // base delay — hero reveals as the loader curtain lifts

const lines = [
  { text: "AI is part of", accent: false },
  { text: "everyday life now.", accent: false },
  { text: "Let's make sure", accent: false },
  { text: "it works for you.", accent: true },
];

const RotatingBadge = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.6 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.8, delay: D + 1.1, ease: [0.22, 1, 0.36, 1] }}
    className="absolute -top-10 -left-6 sm:-left-10 z-10"
    data-testid="hero-rotating-badge"
  >
    <div className="relative h-28 w-28 sm:h-32 sm:w-32">
      <motion.svg
        viewBox="0 0 100 100"
        className="h-full w-full"
        animate={{ rotate: 360 }}
        transition={{ duration: 26, ease: "linear", repeat: Infinity }}
      >
        <defs>
          <path id="badge-circle" d="M50,50 m-38,0 a38,38 0 1,1 76,0 a38,38 0 1,1 -76,0" />
        </defs>
        <text style={{ fontSize: "8px", letterSpacing: "2.4px", fontFamily: "'JetBrains Mono', monospace", fill: "#1C1917" }}>
          <textPath href="#badge-circle" textLength="236" lengthAdjust="spacingAndGlyphs">
            AI FOR WOMEN COMMUNITY • LEARN TOGETHER •
          </textPath>
        </text>
      </motion.svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <img src="/aiwc-logo.png" alt="" className="h-11 w-11 sm:h-12 sm:w-12 rounded-full object-cover shadow-lg" />
      </div>
    </div>
  </motion.div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "14%"]);

  const tiltX = useMotionValue(0);
  const tiltY = useMotionValue(0);
  const rotateX = useSpring(useTransform(tiltY, [-0.5, 0.5], [5, -5]), { stiffness: 140, damping: 18 });
  const rotateY = useSpring(useTransform(tiltX, [-0.5, 0.5], [-5, 5]), { stiffness: 140, damping: 18 });

  const onTilt = (e) => {
    if (!ref.current || !window.matchMedia("(pointer: fine)").matches) return;
    const r = ref.current.getBoundingClientRect();
    tiltX.set((e.clientX - r.left) / r.width - 0.5);
    tiltY.set((e.clientY - r.top) / r.height - 0.5);
  };
  const resetTilt = () => { tiltX.set(0); tiltY.set(0); };

  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      await api.post("/subscribe", { email });
      setDone(true);
      toast.success("Welcome to the community — check your inbox.");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section ref={ref} onMouseMove={onTilt} onMouseLeave={resetTilt}
             className="relative pt-32 md:pt-40 pb-16 md:pb-24 overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7">
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: D - 0.2 }}
                      className="font-mono text-xs uppercase tracking-[0.22em] text-ember font-semibold mb-8"
                      data-testid="hero-eyebrow">
              AI for Women Community — free to join
            </motion.p>
            <h1 className="font-serif font-bold tracking-tight text-ink text-5xl sm:text-6xl lg:text-7xl leading-[1.04]" data-testid="hero-headline">
              {lines.map((line, i) => (
                <span key={i} className="hero-line-mask">
                  <motion.span
                    className={`block ${line.accent ? "italic text-ember" : ""}`}
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.9, delay: D + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                  >
                    {line.text}
                  </motion.span>
                </span>
              ))}
            </h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: D + 0.6 }}
                      className="mt-8 max-w-xl text-base sm:text-lg text-stone-600 leading-relaxed" data-testid="hero-subtext">
              Plain-language AI learning for stay-at-home moms, grandmothers and mothers of every
              kind — what it means for your day-to-day life, your work and your family's safety.
            </motion.p>
            <motion.form onSubmit={submit} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                         transition={{ duration: 0.7, delay: D + 0.75 }}
                         className="mt-10 flex flex-col sm:flex-row gap-3 max-w-xl" data-testid="hero-signup-form">
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                data-testid="hero-email-input"
                className="flex-1 rounded-full border border-linen bg-white px-6 py-4 text-sm text-ink placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-ember/40 focus:border-ember transition-colors duration-300"
              />
              <motion.button type="submit" disabled={busy} data-testid="hero-email-submit"
                      whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}
                      className="inline-flex items-center justify-center gap-2 rounded-full bg-ember text-cream text-sm font-semibold px-8 py-4 hover:bg-ember-dark transition-colors duration-300 disabled:opacity-60">
                {done ? <><Check size={16} /> You're in</> : <>Join free <ArrowRight size={16} /></>}
              </motion.button>
            </motion.form>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: D + 0.95 }}
                      className="mt-4 text-xs text-stone-500">
              One helpful email a week. Unsubscribe anytime. We never share your address.
            </motion.p>
          </div>
          <div className="lg:col-span-5 relative" style={{ perspective: "1200px" }}>
            <RotatingBadge />
            <motion.div
              initial={{ opacity: 0, scale: 0.94, rotate: 2 }} animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 1.1, delay: D + 0.2, ease: [0.22, 1, 0.36, 1] }}
              style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
              className="relative rounded-[2rem] rounded-tr-[5rem] overflow-hidden border border-linen shadow-2xl shadow-stone-900/15"
              data-testid="hero-image-frame">
              <motion.img src={HERO_IMG} alt="Women learning together at home"
                          style={{ y: imgY }} className="w-full h-[420px] sm:h-[520px] object-cover scale-110" />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/25 via-transparent to-transparent" />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 24, rotate: -3 }} animate={{ opacity: 1, y: 0, rotate: -3 }}
                        transition={{ duration: 0.8, delay: D + 0.9 }}
                        className="absolute -bottom-6 -left-4 sm:-left-8 bg-ink text-cream rounded-2xl px-6 py-5 shadow-2xl max-w-[240px]"
                        data-testid="hero-badge">
              <p className="font-serif italic text-lg leading-snug">"You don't need to code. You need to understand."</p>
              <p className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-stone-400">The AIWC promise</p>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
